import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight,
  BadgeCheck,
  BookOpen,
  Brain,
  Check,
  Clock3,
  Download,
  Headphones,
  LibraryBig,
  ShieldCheck,
  Sparkles,
  Star,
  Target,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { PODCAST_CONFIG } from "@/features/podcast/podcast-config";
import {
  getLatestPodcastEpisode,
  isCountedPodcastEpisode,
  parsePodcastEpisodes,
  PODCAST_RSS_REFRESH_INTERVAL_MS,
} from "@/features/podcast/podcast-rss";
import type { PodcastEpisode } from "@/features/podcast/types";
import { useCatalog } from "@/features/sales/storefront/catalog/CatalogContext";
import type { Product } from "@/features/sales/storefront/catalog/types";
import { ProductCard } from "@/features/sales/storefront/components/ProductCard";
import { SiteFooter } from "@/features/sales/storefront/components/SiteFooter";
import { SiteHeader } from "@/features/sales/storefront/components/SiteHeader";
import { appCheckFetch } from "@/lib/firebase";
import { absoluteUrl, SITE_ORIGIN } from "@/lib/seo";

type LatestEpisode = Pick<PodcastEpisode, "title" | "publishedAt" | "url">;

interface HomeReview {
  id: string;
  productId: string;
  productSlug: string;
  productTitle: string;
  rating: number;
  description: string;
  displayName: string;
  verifiedPurchase: boolean;
  updatedAt: string;
}

function getLatestConfiguredEpisode(): LatestEpisode | undefined {
  return [...PODCAST_CONFIG.episodes]
    .filter(isCountedPodcastEpisode)
    .sort(
      (first, second) =>
        new Date(second.publishedAt).getTime() - new Date(first.publishedAt).getTime(),
    )[0];
}

function parseLatestEpisode(xml: string): LatestEpisode | undefined {
  return getLatestPodcastEpisode(parsePodcastEpisodes(xml));
}

async function loadHomeCatalog(): Promise<Product[]> {
  const endpoint = typeof window === "undefined" ? `${SITE_ORIGIN}/api/catalog` : "/api/catalog";
  try {
    const response = await fetch(endpoint, {
      headers: { Accept: "application/json" },
      signal: AbortSignal.timeout(10_000),
    });
    const data = (await response.json().catch(() => null)) as { products?: Product[] } | null;
    if (!response.ok || !Array.isArray(data?.products)) return [];
    return data.products.filter((product) => Boolean(product.id && product.slug && product.title));
  } catch {
    return [];
  }
}

export const Route = createFileRoute("/")({
  loader: loadHomeCatalog,
  head: () => ({
    meta: [
      { title: "Socrattica | Aprende inglés con claridad" },
      {
        name: "description",
        content:
          "Guías digitales y un pódcast para aprender inglés con un método claro, práctico y a tu ritmo.",
      },
      { property: "og:title", content: "Socrattica — Inglés con claridad" },
      {
        property: "og:description",
        content: "Recursos prácticos para dejar de estudiar sin dirección y empezar a avanzar.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: absoluteUrl("/") },
    ],
    links: [{ rel: "canonical", href: absoluteUrl("/") }],
  }),
  component: LandingPage,
});

function LandingPage() {
  const initialProducts = Route.useLoaderData();
  const { products: liveProducts, isLoading } = useCatalog();
  const products = liveProducts.length > 0 ? liveProducts : initialProducts;
  const productsLoading = isLoading && products.length === 0;
  const [latestEpisode, setLatestEpisode] = useState<LatestEpisode | undefined>(
    getLatestConfiguredEpisode,
  );
  const [verifiedReviews, setVerifiedReviews] = useState<HomeReview[]>([]);
  const [shouldLoadReviews, setShouldLoadReviews] = useState(false);
  const reviewsTriggerRef = useRef<HTMLElement>(null);
  const featuredProducts = useMemo(() => {
    return products.filter((product) => product.featured).slice(0, 4);
  }, [products]);
  const sampleProduct = products.find((product) => product.sampleUrl);

  useEffect(() => {
    if (!PODCAST_CONFIG.rssUrl) return;
    const controller = new AbortController();

    async function updateLatestEpisode() {
      try {
        const response = await fetch(PODCAST_CONFIG.rssUrl!, {
          signal: controller.signal,
          cache: "no-store",
          headers: { Accept: "application/rss+xml, application/xml, text/xml" },
        });
        if (!response.ok) throw new Error(`RSS ${response.status}`);
        const episode = parseLatestEpisode(await response.text());
        if (episode) setLatestEpisode(episode);
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") return;
      }
    }

    void updateLatestEpisode();
    const refreshInterval = window.setInterval(
      updateLatestEpisode,
      PODCAST_RSS_REFRESH_INTERVAL_MS,
    );
    return () => {
      controller.abort();
      window.clearInterval(refreshInterval);
    };
  }, []);

  useEffect(() => {
    const trigger = reviewsTriggerRef.current;
    if (!trigger || !("IntersectionObserver" in window)) {
      setShouldLoadReviews(true);
      return;
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (!entry?.isIntersecting) return;
      setShouldLoadReviews(true);
      observer.disconnect();
    });
    observer.observe(trigger);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!shouldLoadReviews) return;
    if (products.length === 0) {
      setVerifiedReviews([]);
      return;
    }

    const controller = new AbortController();

    async function loadVerifiedReviews() {
      const responses = await Promise.allSettled(
        products.map(async (product) => {
          const params = new URLSearchParams({ productId: product.id });
          const response = await appCheckFetch(`/api/product-engagement?${params}`, {
            signal: controller.signal,
            headers: { Accept: "application/json" },
          });
          const data = (await response.json().catch(() => null)) as {
            reviews?: Array<{
              id: string;
              rating: number;
              description: string;
              displayName: string;
              verifiedPurchase: boolean;
              updatedAt: string;
            }>;
          } | null;
          if (!response.ok || !Array.isArray(data?.reviews)) return [];

          return data.reviews
            .filter((review) => review.verifiedPurchase)
            .map((review): HomeReview => ({
              ...review,
              productId: product.id,
              productSlug: product.slug,
              productTitle: product.title,
            }));
        }),
      );

      if (controller.signal.aborted) return;
      setVerifiedReviews(
        responses
          .flatMap((result) => (result.status === "fulfilled" ? result.value : []))
          .sort((first, second) => second.updatedAt.localeCompare(first.updatedAt))
          .slice(0, 3),
      );
    }

    void loadVerifiedReviews();
    return () => controller.abort();
  }, [products, shouldLoadReviews]);

  return (
    <div className="min-h-screen overflow-x-hidden bg-white text-black">
      <SiteHeader />

      <main id="main-content">
        <section className="border-b border-black/10">
          <div className="mx-auto grid max-w-7xl lg:min-h-[550px] lg:grid-cols-[1.2fr_0.8fr]">
            <div className="flex min-w-0 items-center px-4 py-12 sm:px-6 lg:py-16">
              <div className="max-w-3xl">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-black/60">
                  Aprende inglés con claridad
                </p>
                <h1 className="mt-5 text-[2.65rem] font-semibold leading-[1.02] tracking-[-0.06em] sm:text-6xl lg:text-7xl">
                  Menos vueltas.
                  <br />
                  Más progreso.
                </h1>
                <p className="mt-6 max-w-xl text-sm leading-6 text-black/65 sm:text-base">
                  Guías digitales y conversaciones honestas para quienes estudian mucho, pero
                  necesitan un método claro para saber qué practicar y por qué.
                </p>
                <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                  <Button asChild size="lg" className="w-full sm:w-auto">
                    <Link to="/productos/catalogo">
                      Explorar recursos
                      <ArrowRight />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
                    <Link to="/podcast">
                      Empezar gratis
                      <Headphones />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex min-h-80 min-w-0 items-center justify-center overflow-hidden border-t border-black/10 bg-black p-6 text-white sm:p-8 lg:border-l lg:border-t-0">
              <div className="w-full max-w-sm">
                <span className="text-xs font-medium uppercase tracking-[0.16em] text-white/70">
                  Biblioteca digital
                </span>
                <div className="mt-6 border-y border-white/20 py-10">
                  <p className="max-w-xs text-2xl font-semibold leading-tight tracking-[-0.045em] sm:text-3xl">
                    Aprende lo que necesitas. Sin ruido.
                  </p>
                  <p className="mt-4 text-sm leading-6 text-white/70">
                    Elige por nivel, practica a tu ritmo y vuelve a tus materiales desde tu cuenta.
                  </p>
                </div>
                <div className="grid grid-cols-3 py-5 text-center">
                  {["Por nivel", "Práctico", "Descargable"].map((item) => (
                    <span key={item} className="text-xs uppercase tracking-wide text-white/70">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section
          className="border-b border-black/10 py-14 sm:py-18"
          aria-labelledby="featured-title"
        >
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <SectionHeading
              eyebrow="Recursos destacados"
              title="Empieza por lo que hoy te está frenando."
              description="Materiales concretos, organizados por nivel y diseñados para convertirse en práctica."
              action={
                <Button asChild variant="outline">
                  <Link to="/productos/catalogo">
                    Ver toda la biblioteca
                    <ArrowRight />
                  </Link>
                </Button>
              }
            />

            {productsLoading ? (
              <div
                className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
                role="status"
              >
                {[0, 1, 2, 3].map((item) => (
                  <div key={item} className="h-96 animate-pulse rounded-xl bg-black/[0.06]" />
                ))}
                <span className="sr-only">Cargando recursos destacados</span>
              </div>
            ) : featuredProducts.length > 0 ? (
              <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {featuredProducts.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            ) : (
              <div className="mt-8 rounded-xl border border-black/15 p-8 text-center">
                <BookOpen className="mx-auto h-6 w-6" />
                <h3 className="mt-3 font-semibold">No hay recursos destacados</h3>
                <p className="mt-2 text-sm text-black/65">
                  Los recursos aparecerán aquí cuando se marquen como destacados.
                </p>
              </div>
            )}
          </div>
        </section>

        <section className="border-b border-black/10 bg-black text-white">
          <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-18">
            <SectionHeading
              inverse
              eyebrow="Para quién es"
              title="Si estudias, pero no sabes si avanzas."
              description="Socrattica está hecho para transformar esfuerzo disperso en una práctica con intención."
            />
            <div className="mt-8 grid gap-px overflow-hidden rounded-xl border border-white/20 bg-white/20 md:grid-cols-3">
              <AudienceCard
                icon={<Brain />}
                title="Te sabes reglas, pero no las usas"
                description="Conecta la explicación con ejercicios y situaciones que obligan a recuperar lo aprendido."
              />
              <AudienceCard
                icon={<Target />}
                title="Saltas de recurso en recurso"
                description="Trabaja una necesidad concreta, con un alcance visible y sin acumular contenido infinito."
              />
              <AudienceCard
                icon={<Clock3 />}
                title="Necesitas avanzar a tu ritmo"
                description="Descarga el material, repítelo y vuelve a él sin depender de un horario fijo."
              />
            </div>
          </div>
        </section>

        <section className="border-b border-black/10">
          <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 py-14 sm:px-6 sm:py-18 lg:grid-cols-[0.85fr_1.15fr]">
            <div className="mx-auto flex aspect-square w-full max-w-md items-center justify-center overflow-hidden rounded-xl border border-black/10 bg-[#f7f6f1] p-8">
              <img
                src="/socrattica.jpg"
                alt="Emblema de Socrattica, un libro junto a una figura pensante"
                className="h-full w-full object-contain"
                loading="lazy"
              />
            </div>
            <div className="max-w-xl lg:pl-8">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-black/60">
                El autor detrás del método
              </p>
              <h2 className="mt-2 text-4xl font-semibold tracking-[-0.05em] sm:text-5xl">
                Roberto Román Lázaro
              </h2>
              <p className="mt-5 text-base leading-7 text-black/70">
                Es la voz y el autor detrás de Socrattica. Su propuesta parte de una idea sencilla:
                antes de pedir más disciplina, hay que entender cómo practicar y para qué sirve cada
                paso.
              </p>
              <ul className="mt-6 space-y-3 text-sm text-black/70">
                {[
                  "Explicaciones directas, sin promesas mágicas.",
                  "Práctica vinculada a una necesidad concreta.",
                  "Un enfoque basado en evidencia y revisión consciente.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <Check className="mt-0.5 h-4 w-4 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <Button asChild variant="outline" className="mt-7">
                <Link to="/podcast">
                  Conocer el enfoque en el pódcast
                  <ArrowRight />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        <section ref={reviewsTriggerRef} className="border-b border-black/10 bg-[#f7f6f1]">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:px-6 sm:py-18 lg:grid-cols-2">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-black/60">
                Prueba antes de elegir
              </p>
              <h2 className="mt-2 text-4xl font-semibold tracking-[-0.05em]">
                Empieza con una muestra gratuita.
              </h2>
              <p className="mt-4 max-w-xl text-sm leading-6 text-black/70">
                Escucha cómo explicamos el método sin comprar nada. Cuando un producto tenga páginas
                de ejemplo, también podrás abrirlas desde su ficha.
              </p>
              <div className="mt-7 flex flex-col gap-3 sm:flex-row">
                <Button asChild>
                  <Link to="/podcast">
                    Escuchar el tráiler
                    <Headphones />
                  </Link>
                </Button>
                {sampleProduct && (
                  <Button asChild variant="outline">
                    <a href={sampleProduct.sampleUrl} target="_blank" rel="noopener noreferrer">
                      {sampleProduct.sampleLabel || "Ver páginas de ejemplo"}
                      <ArrowRight />
                    </a>
                  </Button>
                )}
              </div>
            </div>
            <div className="rounded-xl border border-black bg-black p-7 text-white sm:p-8">
              <Sparkles className="h-6 w-6" />
              <h3 className="mt-5 text-2xl font-semibold">Reseñas verificadas</h3>
              <p className="mt-3 text-sm leading-6 text-white/75">
                Las opiniones que ves en Socrattica pertenecen a personas que compraron el recurso
                con una cuenta verificada. No publicamos testimonios inventados.
              </p>
            </div>
          </div>
        </section>

        {verifiedReviews.length > 0 && (
          <section className="border-b border-black/10" aria-labelledby="home-reviews-title">
            <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-18">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-black/60">
                  Opiniones reales
                </p>
                <h2
                  id="home-reviews-title"
                  className="mt-2 text-3xl font-semibold tracking-[-0.05em] sm:text-4xl"
                >
                  Reseñas de compradores.
                </h2>
                <p className="mt-3 max-w-2xl text-sm leading-6 text-black/65">
                  Experiencias publicadas por estudiantes que adquirieron estos recursos.
                </p>
              </div>
              <div className="mt-8 grid gap-4 md:grid-cols-3">
                {verifiedReviews.map((review) => (
                  <HomeReviewCard key={`${review.productId}:${review.id}`} review={review} />
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="border-b border-black/10">
          <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-18">
            <SectionHeading
              eyebrow="Cómo funciona"
              title="De la biblioteca a tu cuenta."
              description="Un recorrido simple para elegir, acceder y volver a tus materiales."
            />
            <div className="mt-8 grid gap-4 md:grid-cols-3">
              <ProcessCard
                number="01"
                icon={<LibraryBig />}
                title="Elige por necesidad"
                description="Revisa el nivel, el formato, lo que incluye y la muestra cuando esté disponible."
              />
              <ProcessCard
                number="02"
                icon={<ShieldCheck />}
                title="Usa un correo verificable"
                description="Ese correo vincula de forma privada el pedido con tu cuenta de Socrattica."
              />
              <ProcessCard
                number="03"
                icon={<Download />}
                title="Descarga y vuelve"
                description="Encuentra compras, recibos, archivos y soporte en un único espacio autenticado."
              />
            </div>
          </div>
        </section>

        <section className="border-b border-black/10">
          <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 py-14 sm:px-6 sm:py-18 lg:grid-cols-2">
            <div className="relative mx-auto w-full max-w-md">
              <img
                src={PODCAST_CONFIG.coverImage}
                alt={`Portada de ${PODCAST_CONFIG.name}`}
                className="aspect-square w-full border border-black/10 object-cover"
                loading="lazy"
              />
              <span className="absolute bottom-4 right-4 flex h-12 w-12 items-center justify-center rounded-full bg-white text-black">
                <Headphones className="h-5 w-5" />
              </span>
            </div>

            <div className="max-w-xl lg:pl-8">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-black/60">
                {PODCAST_CONFIG.secondaryTagline}
              </p>
              <h2 className="mt-2 text-4xl font-semibold leading-tight tracking-[-0.05em] sm:text-5xl">
                {PODCAST_CONFIG.name}
              </h2>
              <p className="mt-3 text-base font-medium leading-6 text-black">
                {PODCAST_CONFIG.tagline}
              </p>
              <p className="mt-2 text-sm leading-6 text-black/65">
                {PODCAST_CONFIG.shortDescription}
              </p>

              {latestEpisode && (
                <a
                  href={latestEpisode.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-5 flex items-center justify-between gap-4 border-y border-black/15 py-4 transition-opacity hover:opacity-65"
                >
                  <span>
                    <span className="block text-xs uppercase tracking-[0.14em] text-black/60">
                      Último episodio
                    </span>
                    <span className="mt-1 block text-sm font-semibold">{latestEpisode.title}</span>
                  </span>
                  <ArrowRight className="h-4 w-4 shrink-0" />
                </a>
              )}

              <Button asChild className="mt-7">
                <Link to="/podcast">
                  Ver todos los episodios
                  <ArrowRight />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        <section className="py-14 sm:py-18">
          <div className="mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 lg:grid-cols-[0.65fr_1.35fr]">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-black/60">
                Preguntas frecuentes
              </p>
              <h2 className="mt-2 text-3xl font-semibold tracking-[-0.05em] sm:text-4xl">
                Antes de empezar.
              </h2>
            </div>
            <div className="divide-y divide-black/15 border-y border-black/15">
              <HomeFaq
                question="¿Socrattica es un curso completo?"
                answer="Es una biblioteca de recursos digitales enfocados en necesidades concretas y un pódcast educativo. Cada ficha explica su alcance, nivel y formato."
              />
              <HomeFaq
                question="¿Cómo sé qué recurso elegir?"
                answer="Empieza por el problema que necesitas resolver y revisa el nivel indicado. Si todavía no estás seguro, escucha el pódcast o abre la muestra disponible."
              />
              <HomeFaq
                question="¿Dónde encuentro mis descargas?"
                answer="Inicia sesión con el correo usado en la compra. En Mi cuenta encontrarás pedidos, recibos y archivos disponibles."
              />
              <HomeFaq
                question="¿Cómo recibo ayuda?"
                answer="Desde el centro de soporte puedes crear un ticket privado. El equipo responderá dentro de la misma conversación y conservarás el historial."
              />
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}

function SectionHeading({
  eyebrow,
  title,
  description,
  action,
  inverse = false,
}: {
  eyebrow: string;
  title: string;
  description: string;
  action?: React.ReactNode;
  inverse?: boolean;
}) {
  return (
    <div className="flex flex-col justify-between gap-5 sm:flex-row sm:items-end">
      <div>
        <p
          className={`text-xs font-semibold uppercase tracking-[0.16em] ${
            inverse ? "text-white/70" : "text-black/60"
          }`}
        >
          {eyebrow}
        </p>
        <h2
          id={eyebrow === "Recursos destacados" ? "featured-title" : undefined}
          className="mt-2 max-w-3xl text-3xl font-semibold tracking-[-0.05em] sm:text-4xl"
        >
          {title}
        </h2>
        <p
          className={`mt-3 max-w-2xl text-sm leading-6 ${
            inverse ? "text-white/70" : "text-black/65"
          }`}
        >
          {description}
        </p>
      </div>
      {action}
    </div>
  );
}

function AudienceCard({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <article className="bg-black p-6 sm:p-7">
      <span className="text-white [&_svg]:h-5 [&_svg]:w-5">{icon}</span>
      <h3 className="mt-6 text-xl font-semibold tracking-[-0.03em]">{title}</h3>
      <p className="mt-3 text-sm leading-6 text-white/70">{description}</p>
    </article>
  );
}

function ProcessCard({
  number,
  icon,
  title,
  description,
}: {
  number: string;
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <article className="rounded-xl border border-black/15 p-6">
      <div className="flex items-center justify-between">
        <span className="font-mono text-xs text-black/60">{number}</span>
        <span className="[&_svg]:h-5 [&_svg]:w-5">{icon}</span>
      </div>
      <h3 className="mt-8 text-xl font-semibold tracking-[-0.03em]">{title}</h3>
      <p className="mt-3 text-sm leading-6 text-black/65">{description}</p>
    </article>
  );
}

function HomeReviewCard({ review }: { review: HomeReview }) {
  return (
    <article className="flex h-full flex-col rounded-xl border border-black/15 bg-white p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span
            className="inline-flex gap-0.5"
            role="img"
            aria-label={`${review.rating} de 5 estrellas`}
          >
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                className={`h-4 w-4 ${
                  star <= Math.round(review.rating)
                    ? "fill-amber-400 text-amber-400"
                    : "fill-transparent text-black/20"
                }`}
              />
            ))}
          </span>
          <span className="text-xs font-semibold tabular-nums">{review.rating.toFixed(1)} / 5.0</span>
        </div>
        <span className="flex shrink-0 items-center gap-1.5 text-xs text-black/60">
          <BadgeCheck className="h-3.5 w-3.5 text-emerald-600" />
          Compra verificada
        </span>
      </div>
      <blockquote className="mt-4 flex-1 text-sm leading-6 text-black/75">
        “{review.description}”
      </blockquote>
      <div className="mt-6 border-t border-black/10 pt-4">
        <p className="font-semibold">{review.displayName}</p>
        <Link
          to="/productos/$productId"
          params={{ productId: review.productSlug }}
          className="mt-2 inline-flex text-xs font-semibold underline-offset-4 hover:underline"
        >
          {review.productTitle}
        </Link>
      </div>
    </article>
  );
}

function HomeFaq({ question, answer }: { question: string; answer: string }) {
  return (
    <details className="group py-5">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-sm font-semibold">
        {question}
        <span
          aria-hidden="true"
          className="text-xl font-light transition-transform group-open:rotate-45"
        >
          +
        </span>
      </summary>
      <p className="max-w-2xl pt-3 text-sm leading-6 text-black/65">{answer}</p>
    </details>
  );
}
